import React, { useMemo, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

// ============================================================
// FinanceDashboard — the default entry-point view of the Finance
// workspace. Three sections (Income / Expenses / Net Balance) +
// a member-giving grid + a collapsible "no-giving" section.
//
// Data is derived client-side from the already-fetched `giving`
// and `expenses` arrays passed by the parent FinancePage — no
// extra API round-trip needed.
//
// NET BALANCE RULE (spec item 8): the net balance uses ONLY
// approved expenses. Pending, Rejected, and voided records of
// both types are excluded from every total on this dashboard.
// ============================================================

const PERIODS = ["Day", "Week", "Month", "Year"];
const GIVING_CATEGORIES = ["Regular Offering", "Tithe", "Seed", "Alms", "Other", "Anonymous"];
const EXPENSE_CATEGORIES = [
  "Rent", "Instruments", "Food and Groceries", "Social",
  "Equipment and Materials", "Pastor Stipend", "Other",
];

function periodBounds(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  if (period === "Day") {
    const d = new Date(year, month, now.getDate());
    return { from: d, to: new Date(year, month, now.getDate() + 1) };
  }
  if (period === "Week") {
    const d = new Date(year, month, now.getDate() - 6);
    return { from: d, to: new Date(year, month, now.getDate() + 1) };
  }
  if (period === "Month") {
    return { from: new Date(year, month, 1), to: new Date(year, month + 1, 1) };
  }
  return { from: new Date(year, 0, 1), to: new Date(year + 1, 0, 1) };
}

// Previous equivalent period — used for the delta comparison chips.
function previousPeriodBounds(period) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const day = now.getDate();
  if (period === "Day") {
    const from = new Date(year, month, day - 1);
    const to = new Date(year, month, day);
    return { from, to };
  }
  if (period === "Week") {
    // Previous 7-day span (days -13 through -7 relative to today's start)
    const from = new Date(year, month, day - 13);
    const to = new Date(year, month, day - 6);
    return { from, to };
  }
  if (period === "Month") {
    return { from: new Date(year, month - 1, 1), to: new Date(year, month, 1) };
  }
  return { from: new Date(year - 1, 0, 1), to: new Date(year, 0, 1) };
}

function periodPreviousLabel(period) {
  if (period === "Day") return "yesterday";
  if (period === "Week") return "last week";
  if (period === "Month") return "last month";
  return "last year";
}

function inRange(iso, from, to) {
  if (!iso) return false;
  const d = new Date(iso + (iso.length === 10 ? "T00:00:00" : ""));
  if (Number.isNaN(d.getTime())) return false;
  return d >= from && d < to;
}

function fmt(cents, sym) {
  if (!Number.isFinite(cents)) return `${sym}0.00`;
  return `${sym}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fullName(m) {
  if (!m) return "—";
  return `${m.firstName || ""} ${m.lastName || ""}`.trim() || "—";
}

// Compact number formatter for the Cinzel hero displays — keeps big-money readable.
function heroFmt(cents, sym) {
  return `${sym}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const GOLD = "var(--kc-accent)";
const RED = "#f87171";
const GREEN = "#4ade80";
const AMBER = "#f59e0b";

// Compute the delta between current and previous — returns:
//   { pct: number|null, direction: "up"|"down"|"flat"|"new" }
// pct is null when previous was zero (we render "NEW" chip instead).
function computeDelta(current, previous) {
  const cur = Number(current) || 0;
  const prev = Number(previous) || 0;
  if (prev === 0 && cur === 0) return { pct: 0, direction: "flat" };
  if (prev === 0) return { pct: null, direction: "new" };
  const pct = ((cur - prev) / Math.abs(prev)) * 100;
  if (Math.abs(pct) < 0.05) return { pct: 0, direction: "flat" };
  return { pct, direction: pct > 0 ? "up" : "down" };
}


export default function FinanceDashboard({
  giving,
  expenses,
  members,
  currencySymbol = "$",
  onGoPending,
  onOpenMember,
}) {
  const [period, setPeriod] = useState("Month");
  const [showNoGiving, setShowNoGiving] = useState(false);

  const bounds = useMemo(() => periodBounds(period), [period]);
  const prevBounds = useMemo(() => previousPeriodBounds(period), [period]);

  // ----- Filter data to the selected period (exclude voided) -----
  const filteredGiving = useMemo(
    () => (giving || []).filter(r => !r.voidedAt && inRange(r.date, bounds.from, bounds.to)),
    [giving, bounds]
  );
  const filteredExpenses = useMemo(
    () => (expenses || []).filter(r => !r.voidedAt && inRange(r.date, bounds.from, bounds.to)),
    [expenses, bounds]
  );
  const approvedExpenses = useMemo(
    () => filteredExpenses.filter(r => r.approvalStatus === "Approved"),
    [filteredExpenses]
  );
  const pendingAll = useMemo(
    () => (expenses || []).filter(r => !r.voidedAt && r.approvalStatus === "Pending"),
    [expenses]
  );

  // ----- Previous-period filtered data (for delta chips) -----
  const prevGiving = useMemo(
    () => (giving || []).filter(r => !r.voidedAt && inRange(r.date, prevBounds.from, prevBounds.to)),
    [giving, prevBounds]
  );
  const prevApprovedExpenses = useMemo(
    () => (expenses || []).filter(r => !r.voidedAt && r.approvalStatus === "Approved" && inRange(r.date, prevBounds.from, prevBounds.to)),
    [expenses, prevBounds]
  );

  // ----- INCOME totals + breakdown -----
  const totalIncome = useMemo(
    () => filteredGiving.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [filteredGiving]
  );
  const incomeByCategory = useMemo(() => {
    const buckets = {};
    GIVING_CATEGORIES.forEach(c => { buckets[c] = 0; });
    filteredGiving.forEach(r => {
      const key = r.isAnonymous ? "Anonymous" : (GIVING_CATEGORIES.includes(r.category) ? r.category : "Other");
      buckets[key] = (buckets[key] || 0) + (Number(r.amountCents) || 0);
    });
    return GIVING_CATEGORIES.map(c => ({ category: c, amount: buckets[c] / 100 }));
  }, [filteredGiving]);
  const incomeStats = useMemo(() => {
    const namedGivers = new Set();
    filteredGiving.forEach(r => { if (!r.isAnonymous && r.memberId) namedGivers.add(r.memberId); });
    const largest = filteredGiving.reduce((m, r) => Math.max(m, Number(r.amountCents) || 0), 0);
    const avg = filteredGiving.length > 0 ? Math.round(totalIncome / filteredGiving.length) : 0;
    return {
      count: filteredGiving.length,
      uniqueNamedGivers: namedGivers.size,
      largest,
      average: avg,
    };
  }, [filteredGiving, totalIncome]);

  // ----- EXPENSE totals + breakdown (approved only) -----
  const totalApprovedExpenses = useMemo(
    () => approvedExpenses.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [approvedExpenses]
  );
  const expensesByCategory = useMemo(() => {
    const buckets = {};
    EXPENSE_CATEGORIES.forEach(c => { buckets[c] = 0; });
    approvedExpenses.forEach(r => {
      const key = EXPENSE_CATEGORIES.includes(r.category) ? r.category : "Other";
      buckets[key] = (buckets[key] || 0) + (Number(r.amountCents) || 0);
    });
    return EXPENSE_CATEGORIES.map(c => ({ category: c, amount: buckets[c] / 100 }));
  }, [approvedExpenses]);
  const expenseStats = useMemo(() => {
    const largest = approvedExpenses.reduce((m, r) => Math.max(m, Number(r.amountCents) || 0), 0);
    return { count: approvedExpenses.length, pendingCount: pendingAll.length, largest };
  }, [approvedExpenses, pendingAll]);

  // ----- NET BALANCE (approved only, voided excluded) -----
  const netBalance = totalIncome - totalApprovedExpenses;
  const netPositive = netBalance >= 0;

  // ----- Previous-period totals + deltas for the three headline stats -----
  const prevTotalIncome = useMemo(
    () => prevGiving.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [prevGiving]
  );
  const prevTotalExpenses = useMemo(
    () => prevApprovedExpenses.reduce((s, r) => s + (Number(r.amountCents) || 0), 0),
    [prevApprovedExpenses]
  );
  const prevNetBalance = prevTotalIncome - prevTotalExpenses;

  const incomeDelta = computeDelta(totalIncome, prevTotalIncome);
  const expensesDelta = computeDelta(totalApprovedExpenses, prevTotalExpenses);
  // For Net Balance, direction of "good" depends on sign; we invert semantics if reference is negative — simplest here is to still show the raw delta and colour by up/down direction.
  const netDelta = computeDelta(netBalance, prevNetBalance);
  const prevLabel = periodPreviousLabel(period);

  // ----- MEMBER GIVING GRID -----
  const memberGrid = useMemo(() => {
    const memberMap = {};
    (members || []).forEach(m => { if (m && m.id) memberMap[m.id] = m; });
    const rows = {};
    filteredGiving.forEach(r => {
      if (r.isAnonymous || !r.memberId) return;
      const m = memberMap[r.memberId];
      if (!m) return;
      if (!rows[r.memberId]) {
        rows[r.memberId] = {
          id: r.memberId,
          firstName: m.firstName || "",
          lastName: m.lastName || "",
          "Regular Offering": 0, "Tithe": 0, "Seed": 0, "Alms": 0, "Other": 0, total: 0,
        };
      }
      const cat = GIVING_CATEGORIES.includes(r.category) && r.category !== "Anonymous" ? r.category : "Other";
      const amt = Number(r.amountCents) || 0;
      rows[r.memberId][cat] += amt;
      rows[r.memberId].total += amt;
    });
    return Object.values(rows).sort((a, b) => {
      const ln = (a.lastName || "").localeCompare(b.lastName || "");
      return ln !== 0 ? ln : (a.firstName || "").localeCompare(b.firstName || "");
    });
  }, [filteredGiving, members]);

  // ----- NO-GIVING MEMBERS (active only, sorted alpha) -----
  const noGivingMembers = useMemo(() => {
    const gaveIds = new Set(memberGrid.map(r => r.id));
    return (members || [])
      .filter(m => {
        if (!m || !m.id) return false;
        if (gaveIds.has(m.id)) return false;
        const status = (m.memberStatus || "").toLowerCase();
        // Consider anyone not explicitly marked Inactive as active.
        return status !== "inactive";
      })
      .sort((a, b) => {
        const ln = (a.lastName || "").localeCompare(b.lastName || "");
        return ln !== 0 ? ln : (a.firstName || "").localeCompare(b.firstName || "");
      });
  }, [members, memberGrid]);

  const periodLabel = period === "Day" ? "today"
    : period === "Week" ? "this week"
    : period === "Month" ? "this month"
    : "this year";

  return (
    <div data-testid="finance-dashboard">
      {/* PERIOD TOGGLE ------------------------------------------- */}
      <div
        role="tablist"
        style={{ display: "flex", gap: 4, background: "color-mix(in srgb, var(--kc-text) 6%, transparent)", padding: 4, borderRadius: 10, marginBottom: 20 }}
      >
        {PERIODS.map(p => (
          <button
            key={p}
            className="btn btn-sm"
            style={{
              flex: 1,
              background: period === p ? GOLD : "transparent",
              color: period === p ? "#111" : "var(--kc-text)",
              fontWeight: 700,
              minHeight: 38,
            }}
            onClick={() => setPeriod(p)}
            data-testid={`finance-dash-period-${p}`}
          >
            {p}
          </button>
        ))}
      </div>

      {/* INCOME SECTION ------------------------------------------- */}
      <SectionCard
        title="Income"
        subtitle={`Giving received ${periodLabel}`}
        testid="finance-dash-income"
      >
        <HeroAmount amount={heroFmt(totalIncome, currencySymbol)} color={GOLD} testid="finance-dash-income-total" />
        <DeltaChip delta={incomeDelta} prevLabel={prevLabel} testid="finance-dash-income-delta" />
        <QuickStatsRow
          items={[
            { label: "Transactions", value: incomeStats.count, testid: "income-stat-count" },
            { label: "Unique Named Givers", value: incomeStats.uniqueNamedGivers, testid: "income-stat-unique-givers" },
            { label: "Largest Gift", value: heroFmt(incomeStats.largest, currencySymbol), testid: "income-stat-largest" },
            { label: "Average Gift", value: heroFmt(incomeStats.average, currencySymbol), testid: "income-stat-average" },
          ]}
        />
        <CategoryBarChart
          data={incomeByCategory}
          currencySymbol={currencySymbol}
          testid="finance-dash-income-chart"
        />
      </SectionCard>

      {/* EXPENSES SECTION ---------------------------------------- */}
      <SectionCard
        title="Expenses"
        subtitle={`Approved expenses ${periodLabel}`}
        testid="finance-dash-expenses"
      >
        <HeroAmount amount={heroFmt(totalApprovedExpenses, currencySymbol)} color={GOLD} testid="finance-dash-expenses-total" />
        <DeltaChip delta={expensesDelta} prevLabel={prevLabel} testid="finance-dash-expenses-delta" invertColors />
        <QuickStatsRow
          items={[
            { label: "Approved Records", value: expenseStats.count, testid: "expense-stat-approved-count" },
            {
              label: "Pending Approvals",
              value: expenseStats.pendingCount,
              testid: "expense-stat-pending-count",
              onClick: onGoPending,
              clickable: true,
            },
            { label: "Largest Expense", value: heroFmt(expenseStats.largest, currencySymbol), testid: "expense-stat-largest" },
          ]}
        />
        <CategoryBarChart
          data={expensesByCategory}
          currencySymbol={currencySymbol}
          testid="finance-dash-expenses-chart"
        />
      </SectionCard>

      {/* NET BALANCE SECTION ------------------------------------- */}
      <SectionCard
        title="Net Balance"
        subtitle={period}
        testid="finance-dash-net"
      >
        <HeroAmount
          amount={heroFmt(netBalance, currencySymbol)}
          color={netPositive ? GOLD : RED}
          testid="finance-dash-net-total"
        />
        <DeltaChip delta={netDelta} prevLabel={prevLabel} testid="finance-dash-net-delta" />
        <div style={{ fontSize: 12, opacity: 0.6, textAlign: "center", marginTop: 4 }}>
          Giving received minus approved expenses. Pending, rejected, and voided records are excluded.
        </div>
      </SectionCard>

      {/* MEMBER GIVING GRID ------------------------------------- */}
      <SectionCard
        title="Member Giving Grid"
        subtitle={`Members who gave ${periodLabel}`}
        testid="finance-dash-member-grid"
      >
        {memberGrid.length === 0 ? (
          <div style={{ opacity: 0.6, padding: 20, textAlign: "center" }} data-testid="finance-dash-member-grid-empty">
            No named giving records in this period yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto", marginTop: 6 }}>
            <table
              style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}
              data-testid="finance-dash-member-grid-table"
            >
              <thead>
                <tr style={{ background: "color-mix(in srgb, var(--kc-accent) 10%, transparent)", color: GOLD }}>
                  <th style={thStyle}>Member</th>
                  <th style={thStyleNum}>Regular</th>
                  <th style={thStyleNum}>Tithe</th>
                  <th style={thStyleNum}>Seed</th>
                  <th style={thStyleNum}>Alms</th>
                  <th style={thStyleNum}>Other</th>
                  <th style={{ ...thStyleNum, color: GOLD }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {memberGrid.map(row => (
                  <tr key={row.id}
                    data-testid={`finance-dash-member-row-${row.id}`}
                    style={{ borderBottom: "1px solid color-mix(in srgb, var(--kc-accent) 10%, transparent)", cursor: "pointer" }}
                    onClick={() => onOpenMember && onOpenMember(row.id)}
                  >
                    <td style={tdStyle}>
                      <span style={{ fontWeight: 500 }}>{row.lastName}, {row.firstName}</span>
                    </td>
                    <td style={tdStyleNum}>{fmt(row["Regular Offering"], currencySymbol)}</td>
                    <td style={tdStyleNum}>{fmt(row.Tithe, currencySymbol)}</td>
                    <td style={tdStyleNum}>{fmt(row.Seed, currencySymbol)}</td>
                    <td style={tdStyleNum}>{fmt(row.Alms, currencySymbol)}</td>
                    <td style={tdStyleNum}>{fmt(row.Other, currencySymbol)}</td>
                    <td style={{ ...tdStyleNum, color: GOLD, fontWeight: 700 }}>{fmt(row.total, currencySymbol)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Collapsible NO-GIVING section -------------------- */}
        {noGivingMembers.length > 0 && (
          <div style={{ marginTop: 18, border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)", borderRadius: 10, background: "color-mix(in srgb, var(--kc-text) 3%, transparent)" }}>
            <div
              role="button"
              tabIndex={0}
              onClick={() => setShowNoGiving(v => !v)}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setShowNoGiving(v => !v)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "12px 14px",
                cursor: "pointer",
                userSelect: "none",
              }}
              data-testid="finance-dash-nogiving-toggle"
            >
              <span style={{ transform: showNoGiving ? "rotate(90deg)" : "rotate(0)", transition: "transform 150ms", display: "inline-block" }}>›</span>
              <span style={{ flex: 1, fontSize: 13 }}>
                Members with no giving {periodLabel} — <strong style={{ color: GOLD }}>{noGivingMembers.length}</strong> {noGivingMembers.length === 1 ? "member" : "members"}
              </span>
            </div>
            {showNoGiving && (
              <div
                style={{ padding: "0 14px 14px", display: "flex", flexDirection: "column", gap: 6 }}
                data-testid="finance-dash-nogiving-list"
              >
                {noGivingMembers.map(m => (
                  <div
                    key={m.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => onOpenMember && onOpenMember(m.id)}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onOpenMember && onOpenMember(m.id)}
                    style={{ padding: "6px 8px", borderRadius: 6, cursor: "pointer", fontSize: 13, opacity: 0.85 }}
                    data-testid={`finance-dash-nogiving-item-${m.id}`}
                  >
                    {(m.lastName || "").trim()}{m.lastName ? ", " : ""}{(m.firstName || "").trim() || "—"}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ---------- Presentational helpers ----------

function SectionCard({ title, subtitle, testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{
        background: "color-mix(in srgb, var(--kc-accent) 6%, transparent)",
        border: "1px solid color-mix(in srgb, var(--kc-accent) 18%, transparent)",
        borderRadius: 14,
        padding: 18,
        marginBottom: 18,
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, fontWeight: 700, color: "var(--kc-text)", letterSpacing: "0.10em", textTransform: "uppercase" }}>
          {title}
        </div>
        {subtitle && <div style={{ fontSize: 11, opacity: 0.6, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function HeroAmount({ amount, color, testid }) {
  return (
    <div
      data-testid={testid}
      style={{
        fontFamily: "'Cinzel',serif",
        fontSize: 34,
        fontWeight: 700,
        color,
        textAlign: "center",
        letterSpacing: "0.04em",
        padding: "10px 0 8px",
      }}
    >
      {amount}
    </div>
  );
}

// Delta chip — rendered under each hero total on the dashboard.
// `invertColors` = true for Expenses (rising expenses = red/bad).
function DeltaChip({ delta, prevLabel, testid, invertColors = false }) {
  if (!delta) return null;
  let color, arrow, text, background, borderColor;
  if (delta.direction === "new") {
    // Amber for Expenses (new spend isn't inherently good); green elsewhere.
    color = invertColors ? AMBER : GREEN;
    arrow = "▲";
    text = `NEW vs ${prevLabel}`;
    background = "rgba(0,0,0,0.35)";
    borderColor = `${color}55`;
  } else if (delta.direction === "flat") {
    // Iter-95 Item 2 — the previous grey-on-grey rendering was unreadable
    // in light mode. Switch to the accent-bg / accent tokens so the pill
    // is readable in both themes without changing text content.
    color = "var(--kc-accent)";
    background = "var(--kc-accent-bg)";
    borderColor = "color-mix(in srgb, var(--kc-accent) 35%, transparent)";
    arrow = "—";
    text = `0% vs ${prevLabel}`;
  } else {
    const goodDirection = invertColors ? "down" : "up";
    color = delta.direction === goodDirection ? GREEN : RED;
    arrow = delta.direction === "up" ? "▲" : "▼";
    const abs = Math.abs(delta.pct);
    // Cap display at "999%+" for readability when previous period was tiny.
    const pctText = abs >= 999.5 ? "999%+" : `${abs.toFixed(abs >= 10 ? 0 : 1)}%`;
    text = `${pctText} vs ${prevLabel}`;
    background = "rgba(0,0,0,0.35)";
    borderColor = `${color}55`;
  }
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
      <div
        data-testid={testid}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "3px 10px",
          borderRadius: 999,
          background,
          border: `1px solid ${borderColor}`,
          color,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
        }}
      >
        <span aria-hidden="true">{arrow}</span>
        <span>{text}</span>
      </div>
    </div>
  );
}

function QuickStatsRow({ items }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
      {items.map(it => {
        const Wrapper = it.clickable ? "button" : "div";
        return (
          <Wrapper
            key={it.label}
            data-testid={it.testid}
            onClick={it.onClick}
            style={{
              flex: "1 1 140px",
              minWidth: 140,
              background: "rgba(0,0,0,0.25)",
              border: "1px solid color-mix(in srgb, var(--kc-accent) 15%, transparent)",
              borderRadius: 8,
              padding: 10,
              textAlign: "center",
              cursor: it.clickable ? "pointer" : "default",
              color: "inherit",
              font: "inherit",
            }}
          >
            <div style={{ fontSize: 10, opacity: 0.65, textTransform: "uppercase", letterSpacing: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{it.label}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: it.clickable && Number(it.value) > 0 ? GOLD : "var(--kc-text)", marginTop: 4 }}>
              {it.value}
            </div>
          </Wrapper>
        );
      })}
    </div>
  );
}

function CategoryBarChart({ data, currencySymbol, testid }) {
  const hasData = (data || []).some(d => (d.amount || 0) > 0);
  if (!hasData) {
    return <div style={{ opacity: 0.5, padding: 14, textAlign: "center", fontSize: 12 }} data-testid={`${testid}-empty`}>No records yet.</div>;
  }
  return (
    <div data-testid={testid} style={{ background: "color-mix(in srgb, var(--kc-text) 4%, transparent)", padding: 8, borderRadius: 10 }}>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 34 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="color-mix(in srgb, var(--kc-text) 10%, transparent)" />
          <XAxis
            dataKey="category"
            tick={{ fill: "var(--kc-sub)", fontSize: 10 }}
            interval={0}
            angle={-24}
            textAnchor="end"
            height={50}
          />
          <YAxis
            tick={{ fill: "var(--kc-sub)", fontSize: 10 }}
            tickFormatter={(v) => `${currencySymbol}${v.toLocaleString()}`}
            width={70}
          />
          <Tooltip
            cursor={{ fill: "color-mix(in srgb, var(--kc-accent) 8%, transparent)" }}
            contentStyle={{ background: "var(--kc-surface)", border: "1px solid color-mix(in srgb, var(--kc-accent) 30%, transparent)", borderRadius: 8, color: "var(--kc-text)" }}
            formatter={(v) => [`${currencySymbol}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, "Amount"]}
          />
          <Bar dataKey="amount" fill={GOLD} radius={[6, 6, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

const thStyle = { textAlign: "left", padding: "8px 8px", fontSize: 11, textTransform: "uppercase", letterSpacing: 1, fontWeight: 700 };
const thStyleNum = { ...thStyle, textAlign: "right" };
const tdStyle = { padding: "8px 8px", whiteSpace: "nowrap" };
const tdStyleNum = { padding: "8px 8px", textAlign: "right", whiteSpace: "nowrap", fontFamily: "monospace" };
